import {
  XStack,
  SizableText,
  YStack,
  Spinner,
  Paragraph,
  Stack,
  styled,
  GetProps,
  useMedia,
} from '@mythical/ui'
import { primaryColors } from '@mythical/ui-config/colors'
import useRequester from 'app/hooks/use-requester'
import { likeResult, toggleLike } from 'app/mutations/comments/toggle-like'
import {
  Comment as CommentType,
  createCommentQuery,
  mapToComment,
} from 'app/queries/comments/comments'
import { useStores } from 'app/store'
import { formatDistanceStrict } from 'date-fns'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { Pressable } from 'react-native'

import { Avatar } from '../primitive/Avatar'
import { Button } from '../primitive/Button'
import { IconHeart } from '../primitive/Icon'
import { IconHeartFull } from '../primitive/Icon/icons/IconHeartFull'
import { Menu, MenuItem } from '../primitive/Menu'
import { CommentForm } from './CommentForm'
import { CommentAction, HandleCommentAction } from './CommentsList'
import { ProfileStatsDialog } from '../user/ProfileStatsDialog'

const CommentWrapper = styled(XStack, {
  justifyContent: 'flex-start',
  py: '$3',
  bg: '$secondary10',
  borderRadius: '$3',
  $gtXs: {
    px: '$4',
    py: '$4',
  },
  variants: {
    isReply: {
      true: {
        py: '$0',
        px: '$0',
        pl: '$2',
        $gtXs: {
          px: '$0',
          py: '$0',
          pl: '$0',
        },
      },
    },
  } as const,
})

const LabelText = styled(SizableText, {
  fontSize: '$2',
  color: '$secondary1',
  $gtXs: {
    fontSize: '$3',
  },
})

type CommentWrapperProps = GetProps<typeof CommentWrapper>

interface CommentProps extends CommentWrapperProps {
  item: CommentType
  level: number
  handleAction: HandleCommentAction
  onUpdate?: (all?: boolean) => Promise<void>
}

export const Comment = observer(
  ({
    item: initial,
    level,
    onUpdate,
    handleAction,
    ...props
  }: CommentProps) => {
    const [profileOpen, setProfileOpen] = useState(false)
    const [likeLoading, setLikeLoading] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [item, setItem] = useState(initial)
    const [editing, setEditing] = useState(false)
    const [replying, setReplying] = useState(false)

    useEffect(() => {
      setItem(initial)
    }, [initial])

    const media = useMedia()

    const { userStore } = useStores()
    const { request } = useRequester()

    const refreshComment = async () => {
      const query = createCommentQuery()

      try {
        const result = await request(query, { id: initial.id }, mapToComment)

        if (result && result.id) {
          setItem(result)
        }
      } catch (e) {
        console.error(e)
      }
    }

    const handleLike = async () => {
      setLikeLoading(true)
      const result = await request(toggleLike(), { id: item.id }, likeResult)

      if (result && result.success) {
        await refreshComment()
        setLikeLoading(false)
      }
    }

    const handleMenuAction = (action: CommentAction) => {
      setMenuOpen(false)
      handleAction(action, item)
    }

    const isOwner = userStore?.user?.id === item.memberId

    return (
      <CommentWrapper key={item.id} {...props}>
        <Avatar
          tag="button"
          tabIndex={-1}
          aria-label="View profile"
          cursor="pointer"
          onPress={() => setProfileOpen(true)}
          src={item.profileImage}
          width={40}
          height={40}
          tier={item.tier}
          simple
          $gtXs={{
            mr: '$2',
          }}
          hoverStyle={{
            opacity: 0.8,
          }}
          pressStyle={{
            opacity: 0.75,
          }}
          focusStyle={{
            opacity: 0.75,
          }}
          {...(media.xs && { display: 'none' })}
        />
        <YStack
          flexGrow={1}
          flexShrink={1}
          space="$2"
          ml={'$2'}
          {...(media.gtSm && {
            space: '$4',
            ml: 0,
          })}
        >
          <YStack space="$2" flexGrow={1}>
            <XStack
              space="$2"
              ai="center"
              borderBottomColor="$secondary8"
              borderBottomWidth={1}
              pb="$1"
              $gtXs={{
                pb: '$1',
              }}
            >
              <LabelText
                tag="button"
                aria-label="View profile"
                cursor="pointer"
                fontWeight="600"
                fontSize="$3"
                color="$primary10"
                onPress={() => setProfileOpen(true)}
                hoverStyle={{
                  opacity: 0.8,
                }}
                pressStyle={{
                  opacity: 0.75,
                }}
                focusStyle={{
                  opacity: 0.75,
                }}
              >
                {item.memberName}
              </LabelText>
              <LabelText>
                {formatDistanceStrict(new Date(item.createdAt), new Date())}
                {media.gtXs && ' ago'}
              </LabelText>
              {item.hasEdited && (
                <LabelText fontStyle="italic" fontWeight="300">
                  (Edited)
                </LabelText>
              )}

              <Stack ml="auto">
                {item.memberId && (
                  <Menu
                    open={menuOpen}
                    onOpenChange={(open) => setMenuOpen(open)}
                  >
                    {isOwner && (
                      <MenuItem
                        onPress={() => {
                          setEditing(!editing)
                          setMenuOpen(false)
                        }}
                      >
                        {editing ? 'Cancel' : 'Edit'}
                      </MenuItem>
                    )}
                    {isOwner && (
                      <MenuItem onPress={() => handleMenuAction('delete')}>
                        Delete
                      </MenuItem>
                    )}
                    {!isOwner && (
                      <MenuItem onPress={() => handleMenuAction('report')}>
                        Report
                      </MenuItem>
                    )}
                    {!isOwner && (
                      <MenuItem onPress={() => handleMenuAction('block')}>
                        Block
                      </MenuItem>
                    )}
                  </Menu>
                )}
              </Stack>
            </XStack>
            <YStack space="$3" flexShrink={1}>
              {!editing && (
                <Paragraph
                  color="white"
                  space="$1"
                  size="$3"
                  fontWeight={item.content ? '400' : '300'}
                  $gtXs={{
                    size: '$4',
                    fontWeight: item.content ? '400' : '300',
                  }}
                  fontStyle={item.content ? 'normal' : 'italic'}
                  whiteSpace="pre-wrap"
                  selectable
                >
                  {item.content || 'Deleted Comment'}
                </Paragraph>
              )}
              {editing && (
                <CommentForm
                  px="$0"
                  $gtXs={{
                    px: '$0',
                  }}
                  id={item.id}
                  comment={item.content}
                  isEditing
                  onCancel={() => setEditing(false)}
                  onSubmitted={async () => {
                    await onUpdate?.()
                    setEditing(false)
                  }}
                />
              )}

              {!editing && !item.deleted && (
                <XStack ai="center" space="$2">
                  <Button
                    p="$2"
                    h="$3"
                    theme="tertiary"
                    scaleSpace={0.4}
                    onPress={handleLike}
                    icon={
                      likeLoading ? (
                        <Spinner
                          color={primaryColors.primary8}
                          px="$1.5"
                          scale={1.2}
                        />
                      ) : item.hasLiked ? (
                        <IconHeartFull size={20} color="$primary6" />
                      ) : (
                        <IconHeart size={20} color="$primary6" />
                      )
                    }
                  >
                    {`${item.likes}`}
                  </Button>
                  {level < 3 && (
                    <Pressable onPress={() => setReplying(!replying)}>
                      <SizableText
                        py="$1"
                        px="$2"
                        fontWeight="600"
                        fontSize="$4"
                        color="$primary1"
                      >
                        Reply
                      </SizableText>
                    </Pressable>
                  )}
                </XStack>
              )}
            </YStack>
          </YStack>
          {replying && (
            <CommentForm
              p="$0"
              $gtXs={{
                p: '$0',
              }}
              parent={item.id}
              onSubmitted={async () => {
                await onUpdate?.()
                setReplying(false)
              }}
            />
          )}

          {item.replies && item.replies.length > 0 && (
            <YStack width="100%" space="$2">
              {item.replies.map((reply: CommentType) => (
                <Comment
                  isReply
                  item={reply}
                  key={reply.id}
                  level={level + 1}
                  onUpdate={onUpdate}
                  handleAction={handleAction}
                />
              ))}
            </YStack>
          )}
        </YStack>
        <ProfileStatsDialog
          open={false}
          onOpenChange={setProfileOpen}
          userId={item.memberId}
        />
      </CommentWrapper>
    )
  }
)
